/**
 * OrdersPage — the customer's order history.
 *
 * Reads GET /orders, which is scoped to the signed-in account server-side, so
 * this page never filters by customer itself. Guests get a sign-in prompt
 * rather than an empty list, because "no orders" and "not signed in" are very
 * different messages to show someone who just paid.
 */

import { Link } from 'react-router-dom';
import { Receipt, LogIn, Clock } from 'lucide-react';
import {
  ORDER_STATUS,
  ORDER_STATUS_LABEL,
  PAYMENT_STATUS,
  formatCurrency,
  productSizeLabel,
} from '@rocket07/shared';
import { Container, PageHeader } from '@/components/layout/Container';
import { EmptyState, ErrorState, Button, Badge, Skeleton } from '@/components/ui';
import { useCustomerAuth } from '@/context/CustomerAuthContext';
import { useMyOrders } from '@/hooks/useOrders';
import { ROUTES } from '@/constants/routes';

/** Status → badge colour. Anything unmapped falls back to neutral grey. */
const STATUS_TONE = {
  [ORDER_STATUS.PENDING]: 'warning',
  [ORDER_STATUS.CONFIRMED]: 'primary',
  [ORDER_STATUS.PACKED]: 'primary',
  [ORDER_STATUS.OUT_FOR_DELIVERY]: 'primary',
  [ORDER_STATUS.DELIVERED]: 'success',
  [ORDER_STATUS.CANCELLED]: 'danger',
  [ORDER_STATUS.FAILED]: 'danger',
};

export function OrdersPage() {
  const { status: authStatus } = useCustomerAuth();
  const { data: orders = [], isLoading, isError, error, refetch } = useMyOrders();

  if (authStatus === 'unauthenticated') {
    return (
      <Container>
        <PageHeader title="My Orders" subtitle="Track and reorder your past orders" />
        <EmptyState
          icon={LogIn}
          title="Sign in to see your orders"
          description="Your order history is tied to your account, so we need to know who you are before we can show it."
          action={
            <Link to={ROUTES.login}>
              <Button>Sign in</Button>
            </Link>
          }
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader title="My Orders" subtitle="Track and reorder your past orders" />

      {isLoading && (
        <div className="space-y-3">
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-36 w-full rounded-2xl" />
          ))}
        </div>
      )}

      {isError && <ErrorState error={error} onRetry={refetch} />}

      {!isLoading && !isError && orders.length === 0 && (
        <EmptyState
          icon={Receipt}
          title="No orders yet"
          description="Once you place an order it will appear here with live status."
          action={
            <Link to={ROUTES.home}>
              <Button>Start an order</Button>
            </Link>
          }
        />
      )}

      {!isLoading && !isError && orders.length > 0 && (
        <ul className="space-y-3">
          {orders.map((order) => (
            <OrderCard key={order._id} order={order} />
          ))}
        </ul>
      )}
    </Container>
  );
}

function OrderCard({ order }) {
  const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0);
  const isPaid = order.payment?.status === PAYMENT_STATUS.PAID;

  return (
    <li className="rounded-2xl border border-line bg-surface p-4 shadow-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">#{order.reference}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted">
            <Clock size={13} />
            {formatOrderDate(order.createdAt)}
          </p>
        </div>
        <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>
          {ORDER_STATUS_LABEL[order.status] ?? order.status}
        </Badge>
      </div>

      {/* Line items — the snapshot taken at checkout, not today's catalogue. */}
      <ul className="mt-3 space-y-1.5 border-t border-line pt-3 text-sm">
        {order.items.map((item, index) => (
          <li key={`${order._id}-${index}`} className="flex items-center justify-between gap-3">
            <span className="min-w-0 truncate text-ink">
              {item.name}
              {productSizeLabel(item) ? (
                <span className="text-muted"> · {productSizeLabel(item)}</span>
              ) : null}
            </span>
            <span className="shrink-0 text-muted">
              ×{item.quantity} · {formatCurrency(item.price * item.quantity)}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex items-center justify-between border-t border-dashed border-line pt-3">
        <span className="text-xs text-muted">
          {itemCount} item{itemCount > 1 ? 's' : ''} ·{' '}
          {isPaid ? (
            <span className="font-semibold text-primary-700">Paid</span>
          ) : (
            <span className="font-semibold text-danger">Payment pending</span>
          )}
        </span>
        <span className="text-base font-extrabold text-ink">{formatCurrency(order.total)}</span>
      </div>
    </li>
  );
}

/** "25 Jul, 4:30 pm" — enough to recognise an order without a full timestamp. */
function formatOrderDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default OrdersPage;
